import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Subscription } from 'rxjs';

import { adhocService } from '@unpispas/upp-base';
import { geocodeService } from '@unpispas/upp-base';
import { languageService, languageSupport } from '@unpispas/upp-base';
import { pushService } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';

import { AskWaiter } from '@unpispas/upp-data';
import { AskWaiterView } from '@unpispas/upp-data';
import { dataService } from '@unpispas/upp-data';
import { ExtraView } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';
import { Ticket } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

import { ViewRenderer } from '@unpispas/upp-wdgt';

import { OrderCloseReason } from '../order/upp-order';
import { ToPayCloseEvent, ToPayCloseReason, UppGuestPaymentComponent } from '../payment/upp-payment';

/********************************/
/* Config banner keys           */
/********************************/

const DISPLAY_OPTION_KEYS: ReadonlyArray<string> = [
    'optionTableService',
    'optionCreditCard',
    'optionOnlinePay',
    'optionQrOrders',
];

const SOCIAL_LINKS: ReadonlyArray<string> = [
    'homepage',
    'facebook',
    'twitter',
    'instagram',
    'tripadvisor',
];

/********************************/
/* Active home panel            */
/********************************/

type GuestHomePanel = 'MENU' | 'ORDER' | 'PAYMENT' | 'WAITER' | 'RECEIPT';

/********************************/
/* Capability tile (presentation) */
/********************************/

type GuestOptionTone = 'ok' | 'nok' | 'warn';

type GuestOptionTile = {
    opkey: string;
    icon: string;
    labelKey: string;
    titleKey: string;
    messageKey: string;
    messageArgs: Array<string | number>;
    tone: GuestOptionTone;
    badgeColor: 'success' | 'danger' | 'warning';
    badgeName: string;
};

/** Guest home-of-table screen (mounted when `view.View === 'PLACE'`). */
@Component({
    selector: 'upp-guest-home',
    templateUrl: './upp-home.html',
    styleUrls: ['./upp-home.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class UppGuestHomeComponent extends languageSupport implements OnInit, OnDestroy {
    /********************************/
    /* Social / menu state          */
    /********************************/

    readonly socialLinks = SOCIAL_LINKS;

    private _showLangMenu = false;
    get ShowLangMenu(): boolean {
        return this._showLangMenu;
    }

    /********************************/
    /* Active panel                 */
    /********************************/

    private _panel: GuestHomePanel = 'MENU';
    get Panel(): GuestHomePanel {
        return this._panel;
    }

    private _onlinePayAlive = false;
    get OnlinePayAlive(): boolean {
        return this._onlinePayAlive;
    }

    private _onlineWait = false;
    get OnlineWait(): boolean {
        return this._onlineWait && this._onlinePayAlive;
    }

    @ViewChild(UppGuestPaymentComponent) private _payment: UppGuestPaymentComponent | null = null;

    get OnlinePayTickets(): TicketView[] {
        const held = this._payment?.HeldOnlineTickets ?? [];
        if (held.length > 0) {
            return held;
        }

        return (this.qrcode?.ToPayTickets ?? []).filter(
            (ticket) => ticket.payment === 'PAYLINE',
        );
    }

    private _heldWaiterPayTickets: TicketView[] = [];
    get WaiterPayTickets(): TicketView[] {
        return this._heldWaiterPayTickets;
    }

    private SetPanel(value: GuestHomePanel): void {
        this._panel = value;
        this.change.markForCheck();
    }

    OnOnlinePayChange(active: boolean): void {
        this._onlinePayAlive = active;
        if (!active) {
            this._onlineWait = false;
        }
        this.change.markForCheck();
    }

    /********************************/
    /* Order ticket                 */
    /********************************/

    private get PendingTicket(): TicketView | null {
        return this.qrcode?.tickets.find(
            (ticket) => (ticket.session === this.data.session)
                && (ticket.status === 'PR')
                && ticket.IsRecent,
        ) ?? null;
    }

    private _ordertkt: TicketView | null = null;
    get OrderTicket(): TicketView | null {
        return this._ordertkt;
    }

    /********************************/
    /* Subscriptions                */
    /********************************/

    private _qrcode_subscription: Subscription | null = null;
    private _place_subscription: Subscription | null = null;
    private _change_subscription: Subscription | null = null;

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        private data: dataService,
        private adhoc: adhocService,
        private toast: toastService,
        private geocode: geocodeService,
        private push: pushService,
        public view: viewService,
    ) {
        super(lang, change.cdref);
    }

    /********************************/
    /* Model accessors              */
    /********************************/

    get qrcode(): QrCodeView | null {
        return this.data.alives.get(this.data.qrcode) as QrCodeView | null;
    }

    get place(): PlaceView | null {
        return this.qrcode?.place ?? null;
    }

    get DisplayOptions(): Array<string> {
        if (!this.place) {
            return [];
        }

        return [...DISPLAY_OPTION_KEYS];
    }

    private get PendingExtras(): ExtraView[] {
        const _place = this.place;
        const _qrcode = this.qrcode;
        if (!_place || !_qrcode) {
            return [];
        }

        return _place.extras.filter(
            (extra) => extra.IsActive && extra.qrcodes.includes(_qrcode),
        );
    }

    get HasPendingExtras(): boolean {
        return this.PendingExtras.length > 0;
    }

    get PendingExtrasPrice(): number | null {
        let _amount = 0;

        for (const extra of this.PendingExtras) {
            if (extra.type !== 'TICKETFIXED') {
                return null;
            }
            _amount += extra.price || 0;
        }

        return Math.round(_amount * 100) / 100;
    }

    get CanViewMenu(): boolean {
        return !!this.place?.PdfMenu;
    }

    get CanMakeOrder(): boolean {
        return !!this.place && !this.place.IsExpired && (this.place.IsBarOpen || this.place.IsKitOpen);
    }

    get CanPayBill(): boolean {
        if (!this.place || this.place.IsExpired) {
            return false;
        }

        return this._onlinePayAlive
            || (this.qrcode?.UnpaidTickets.length ?? 0) > 0
            || (this.qrcode?.ToPayTickets.length ?? 0) > 0;
    }

    get CanCallWaiter(): boolean {
        return !!this.place && !this.place.IsExpired && this.place.TableService;
    }

    get HasWaiterRequest(): boolean {
        if ((this.qrcode?.requests.length ?? 0) > 0) {
            return true;
        }

        return this._heldWaiterPayTickets.some((ticket) => ticket.IsToPay);
    }

    get CanReceipt(): boolean {
        return !!this.place && !this.place.IsExpired && (this.qrcode?.PaidTickets.length ?? 0) > 0;
    }

    get OrdersViaQr(): boolean {
        return !!this.place?.AcceptQR;
    }

    /********************************/
    /* Lifecycle                    */
    /********************************/

    ngOnInit(): void {
        this._BindTableRefresh();

        this._change_subscription = this.data.OnQrCodeChanged.subscribe(
        () => {
            this._BindTableRefresh();
            this.change.markForCheck();
        });
    }

    ngOnDestroy(): void {
        if (this._qrcode_subscription != null) {
            this._qrcode_subscription.unsubscribe();
            this._qrcode_subscription = null;
        }

        if (this._place_subscription != null) {
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this._change_subscription != null) {
            this._change_subscription.unsubscribe();
            this._change_subscription = null;
        }

        if (this._geolocation_subscription) {
            this._geolocation_subscription.unsubscribe();
            this._geolocation_subscription = null;
        }

        this.geocode.NearBy(null);
        super.OnDestroy();
    }

    private _BindTableRefresh(): void {
        if (this._qrcode_subscription != null) {
            this._qrcode_subscription.unsubscribe();
            this._qrcode_subscription = null;
        }

        if (this._place_subscription != null) {
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        const _qrcode = this.qrcode;
        if (_qrcode) {
            this._qrcode_subscription = _qrcode.OnRefresh.subscribe(
            () => {
                void this.CheckAskWaiter();
                this.CheckAction();
                this.change.markForCheck();
            });
        }

        const _place = this.place;
        if (_place) {
            this._place_subscription = _place.OnRefresh.subscribe(
            () => {
                void this.CheckAskWaiter();
                this.CheckAction();
                this.change.markForCheck();
            });
        }

        void this.CheckAskWaiter();
        this.CheckAction();
    }

    /********************************/
    /* Language picker              */
    /********************************/

    get CurrentLanguage(): string {
        return this.lang.GetLanguage(true);
    }

    get Languages(): string[] {
        return this.lang.GetLanguages();
    }

    LanguageFlag(language: string): string {
        const _flag = this.lang.FlagFor(language);
        if (!_flag) {
            return 'fi';
        }
        return 'fi fi-' + _flag.toLowerCase() + ' fis';
    }

    OnToggleLangMenu(): void {
        this._showLangMenu = !this._showLangMenu;
        this.change.markForCheck();
    }

    async OnSelectLanguage(code: string): Promise<void> {
        const _code = String(code || '').toUpperCase();
        this._showLangMenu = false;
        if (!_code || _code === this.CurrentLanguage) {
            this.change.markForCheck();
            return;
        }

        await this.lang.SetLanguage(_code, true);
        this.change.markForCheck();
    }

    LangTrack(_index: number, code: string): string {
        return code;
    }

    /********************************/
    /* Social links                 */
    /********************************/

    HasPlaceLink(link: string): boolean {
        const _row = this.place?.GetPlaceLink(link) ?? null;
        return !!(_row?.url);
    }

    OnPlaceLink(link: string): void {
        const _url = this.place?.GetPlaceLink(link)?.url ?? null;
        if (!_url) {
            return;
        }

        const _win = window.open(_url, '_blank');
        _win?.focus();
    }

    /********************************/
    /* PDF menu                     */
    /********************************/

    async OnViewMenu(): Promise<void> {
        const _place = this.place;
        const _pdfmenu = _place?.PdfMenu ?? null;
        const _url = _pdfmenu?.url ?? null;
        if (!_place || !_url) {
            return;
        }

        const _win = window.open(_url, '_blank');
        if (_win) {
            _win.focus();
            return;
        }

        const filename = (_place.name || 'menu') + ' -  menu.pdf';
        try {
            const _ok = await this.adhoc.Download(_url, filename, {
                'Accept': 'application/pdf',
                'Content-Type': 'application/pdf',
            }, false, '_blank');
            if (!_ok) {
                this.toast.ShowAlert('danger', this.lang.tr('@file_not_found'));
            }
        }
        catch (error) {
            this.toast.ShowAlert('danger', this.lang.tr('@file_not_found'));
            console.error('download error: ', error);
        }
    }

    /********************************/
    /* Action entries               */
    /********************************/

    OnMakeOrder(): void {
        if (!this.place || !this.qrcode) {
            return;
        }

        let ticket = this._ordertkt || this.PendingTicket;
        if (!ticket) {
            const _ticket = new Ticket(null, this.data);
            _ticket.session = this.data.session;
            _ticket.place = this.place.object;
            _ticket.qrcode = this.qrcode.object;
            _ticket.status = 'PR';
            ticket = this.data.alives.get(_ticket) as TicketView;
        }

        this._ordertkt = ticket;
        this.SetPanel('ORDER');
    }

    OnOrderClosed(reason: OrderCloseReason): void {
        if (reason === 'back') {
            this.SetPanel('MENU');
            return;
        }

        const goPay = reason === 'sent' && !!this._ordertkt && !this._ordertkt.IsEmpty;
        this._ordertkt = null;
        this.SetPanel(goPay ? 'PAYMENT' : 'MENU');
        if (reason === 'sent') {
            this._RegisterPush();
        }
    }

    private _pushregistered = false;
    private _showPushDenied = false;

    get ShowPushDenied(): boolean {
        return this._showPushDenied;
    }

    private async _RegisterPush(): Promise<void> {
        if (this._pushregistered) {
            return;
        }
        this._pushregistered = true;
        const ok = await this.push.RegisterToken();
        if (!ok) {
            this._showPushDenied = true;
            this.change.markForCheck();
        }
    }

    OnClosePushDenied(): void {
        this._showPushDenied = false;
        this.change.markForCheck();
    }

    OnPayBill(): void {
        this.SetPanel('PAYMENT');
    }

    /** Accepts structured Close payload or legacy reason string. */
    OnPaymentClosed(event: ToPayCloseEvent | ToPayCloseReason): void {
        const payload: ToPayCloseEvent = typeof event === 'string'
            ? { reason: event }
            : (event && typeof event === 'object' && 'reason' in event)
                ? event
                : { reason: 'back' };
        const reason = payload.reason;
        const eventFocus = payload.pendingWaitFocus;

        if (reason === 'paid' && this.place?.TableService) {
            this._ordertkt = null;
            this._onlineWait = false;
            this._requestwaiting = true;
            this._waiterDismissed = false;
            // Only the tickets in this payment request — never other PP rows on the table.
            this._heldWaiterPayTickets = this._CaptureWaiterPayTickets(eventFocus);
            this.SetPanel('WAITER');
            void this.ControlNearBy(true);
            return;
        }

        if (reason === 'pending-wait') {
            this._ordertkt = null;
            // Align with OnlineWait getter: only Stripe-alive wait clears cash/card focus.
            // Never treat leftover PAYLINE ToPayTickets as onlineWait (that wiped focus
            // and opened unscoped WAITER with every AskWaiter on the table).
            this._onlineWait = this._onlinePayAlive;
            this._requestwaiting = !this._onlineWait;
            this._waiterDismissed = false;
            // Row tap / paid emit pendingWaitFocus → hold only those tickets.
            // No focus → empty hold (never expand to every PP on the table).
            this._heldWaiterPayTickets = this._onlineWait
                ? []
                : this._CaptureWaiterPayTickets(eventFocus);
            this.SetPanel('WAITER');
            if (!this._onlineWait && this.place?.TableService) {
                void this.ControlNearBy(true);
            }
            return;
        }

        if (reason === 'paid-online') {
            this._ordertkt = null;
            this._requestwaiting = false;
            this._onlineWait = false;
            this._heldWaiterPayTickets = [];
            if (this.CanReceipt) {
                this.SetPanel('RECEIPT');
                return;
            }
        }

        this.OnClosePanel();
    }

    /**
     * Payment-request focus for WAITER. Only tickets the diner just chose to pay
     * (or tapped as pending). Never expand to other `IsToPay` rows on the table —
     * combining unrelated tickets into one bill has fiscal/legal risk.
     */
    private _CaptureWaiterPayTickets(eventFocus?: TicketView[]): TicketView[] {
        if (eventFocus && eventFocus.length > 0) {
            return [...eventFocus];
        }

        const _focus = [...(this._payment?.PendingWaitFocus ?? [])];
        if (_focus.length > 0) {
            return _focus;
        }

        return [];
    }

    /********************************/
    /* Waiter proximity             */
    /********************************/

    private _geolocation_subscription: Subscription | null = null;

    private _nearbycontrol = false;
    private _nearbywaiting = false;
    private _askwaiter_checking = false;
    private _requestwaiting = false;
    /** Set when the diner Continues from WAITER; MENU shows pending status until they reopen or requests clear. */
    private _waiterDismissed = false;
    /** Blocks CheckAskWaiter from reopening WAITER while cancel flush runs. */
    private _cancellingWaiter = false;

    private CheckNearBy(): void {
        if (this._nearbywaiting || !this.place?.Coordinates) {
            return;
        }

        const _distance = this.geocode.DistanceTo(this.place.Coordinates);
        if (_distance > (250 + this.geocode.CurrentPosition.accuracy)) {
            this._nearbywaiting = true;
            this.geocode.WaitForNear(this.place.Coordinates).then(async (data) => {
                if (!data) {
                    await this.ControlNearBy(false);
                }
                this._nearbywaiting = false;
            });
        }
    }

    private async ControlNearBy(enable: boolean): Promise<boolean> {
        if (!enable) {
            this._StopNearByWatch();
            await this.CancelAskWaiter();
            return false;
        }

        if (this._nearbycontrol) {
            return true;
        }

        if (this.place?.QrGeolocation === false) {
            return true;
        }

        this._nearbycontrol = !!this.place?.Coordinates && await this.geocode.NearBy(this.place.Coordinates);
        if (this._nearbycontrol) {
            this._geolocation_subscription = this.geocode.OnPositionChanged.subscribe(() => {
                this.CheckNearBy();
            });
        }
        return this._nearbycontrol;
    }

    /** Stops proximity watch without cancelling waiter/pay requests. */
    private _StopNearByWatch(): void {
        this.geocode.NearBy(null);

        if (this._geolocation_subscription) {
            this._geolocation_subscription.unsubscribe();
            this._geolocation_subscription = null;
        }

        this._nearbycontrol = false;
    }

    private async CancelAskWaiter(): Promise<void> {
        const _focus = this._heldWaiterPayTickets;
        const _focusSet = _focus.length > 0 ? new Set(_focus) : null;
        const _allRequests = [...(this.qrcode?.requests ?? [])];
        const _requests = _focusSet
            ? _allRequests.filter(
                (request) => !!request.ticket
                    && this._IsLinkedPayTicket(request.ticket, _focusSet),
            )
            : _allRequests;
        // Focus: CancelPayment every held PP ticket (cash/card AskWaiter orphans +
        // stale PAYLINE with no live Stripe session). No focus: clear orphans.
        const _pending = _focusSet
            ? _focus.filter((ticket) => ticket.IsToPay)
            : this._PendingPayTicketsToClear(_requests);

        if (_requests.length === 0 && _pending.length === 0) {
            return;
        }

        const _transaction = this.data.transaction;
        for (const _request of _requests) {
            this.RevertTicketRequest(_request);
            _request.status = 'DE';
        }

        for (const _request of _requests) {
            if (_request.ticket) {
                await _transaction.push(_request.ticket.object);
            }
            await _transaction.push(_request.object);
        }

        for (const _ticket of _pending) {
            if (!_ticket.IsToPay) {
                continue;
            }
            _ticket.CancelPayment();
            if (_ticket.IsPrepay) {
                _ticket.status = 'PR';
            }
            await _transaction.push(_ticket.object);
        }

        this._heldWaiterPayTickets = [];
        await _transaction.flush();

        this.change.markForCheck();
    }

    private _PendingPayTicketsToClear(requests: AskWaiterView[]): TicketView[] {
        const _linked = this._LinkedPayTickets(requests);
        // Prefer tickets not already reverted via AskWaiter (instance or _uuid).
        const _extra = [
            ...this._heldWaiterPayTickets,
            ...this._OrphanPendingPayTickets(requests),
        ];
        const _seen = new Set<TicketView>();
        const _pending: TicketView[] = [];
        for (const ticket of _extra) {
            if (!ticket || _seen.has(ticket) || this._IsLinkedPayTicket(ticket, _linked)) {
                continue;
            }
            if (ticket.IsToPay) {
                _seen.add(ticket);
                _pending.push(ticket);
            }
        }
        return _pending;
    }

    private _LinkedPayTickets(requests: AskWaiterView[]): Set<TicketView> {
        const _linked = new Set<TicketView>();
        for (const _request of requests) {
            if (_request.ticket
                && ((_request.reason === 'PAYCASH') || (_request.reason === 'PAYCARD'))) {
                _linked.add(_request.ticket);
            }
        }
        return _linked;
    }

    private _IsLinkedPayTicket(ticket: TicketView, linked: Set<TicketView>): boolean {
        if (linked.has(ticket)) {
            return true;
        }

        const _key = ticket._uuid || ticket.objid;
        if (!_key) {
            return false;
        }

        for (const _linked of linked) {
            if ((_linked._uuid || _linked.objid) === _key) {
                return true;
            }
        }

        return false;
    }

    private _OrphanPendingPayTickets(requests: AskWaiterView[]): TicketView[] {
        const _linked = this._LinkedPayTickets(requests);
        const _source = [
            ...(this.qrcode?.ToPayTickets ?? []),
            ...(this.qrcode?.tickets ?? []),
        ];
        const _seen = new Set<TicketView>();
        const _orphans: TicketView[] = [];
        for (const ticket of _source) {
            if (!ticket || _seen.has(ticket) || this._IsLinkedPayTicket(ticket, _linked)) {
                continue;
            }
            if (ticket.IsToPay) {
                _seen.add(ticket);
                _orphans.push(ticket);
            }
        }
        return _orphans;
    }

    private async CheckAskWaiter(): Promise<void> {
        if (!this.place?.TableService || this._cancellingWaiter) {
            return;
        }

        const _requests = this.qrcode?.requests ?? [];
        if (_requests.length === 0) {
            this._waiterDismissed = false;
            if (!this._requestwaiting) {
                // No live requests and not holding for pay rows → drop ticket focus.
                this._heldWaiterPayTickets = [];
            }
            if (this.Panel === 'WAITER' && !this._requestwaiting && !this._onlineWait) {
                this.SetPanel('MENU');
            }
            this.change.markForCheck();
            return;
        }

        this._requestwaiting = false;
        // Keep payment focus hold so a single-ticket tap does not expand to the
        // whole table; clear only when Continuar/cancel or requests go empty.

        // Do not unmount PAYMENT / ORDER / RECEIPT, or WAITER while Stripe wait is showing.
        if (this.Panel === 'PAYMENT' || this.Panel === 'ORDER' || this.Panel === 'RECEIPT') {
            return;
        }

        if (this.Panel === 'WAITER' && this._onlineWait) {
            return;
        }

        if (this._waiterDismissed) {
            this.change.markForCheck();
            return;
        }

        this.SetPanel('WAITER');

        if (this._askwaiter_checking) {
            return;
        }

        this._askwaiter_checking = true;
        await this.ControlNearBy(true);
        this._askwaiter_checking = false;
    }

    async OnCallWaiter(): Promise<void> {
        if (this.HasWaiterRequest) {
            this._waiterDismissed = false;
            this.SetPanel('WAITER');
            void this.ControlNearBy(true);
            return;
        }

        if (!(await this.ControlNearBy(true))) {
            await this.ControlNearBy(false);
            return;
        }

        if ((this.qrcode?.requests.length ?? 0) === 0) {
            const _waiter = new AskWaiter(null, this.data);
            _waiter.session = this.data.session ?? null;
            _waiter.qrcode = this.qrcode?.object ?? null;
            _waiter.status = 'AC';

            const _transaction = this.data.transaction;
            await _transaction.push(_waiter);
            await _transaction.flush();
        }

        this._waiterDismissed = false;
        this.SetPanel('WAITER');
    }

    async OnEndWaiter(cancel: boolean): Promise<void> {
        if (this._onlineWait) {
            this._payment?.DismissOnlineWait();
            this._onlineWait = false;
            this.SetPanel('MENU');
            return;
        }

        if (cancel) {
            // Cancel directly (not only via ControlNearBy) and block CheckAskWaiter
            // from reopening WAITER while the DE/CancelPayment flush is in flight.
            // Stay dismissed after cancel so a lagging refresh cannot remount WAITER.
            this._cancellingWaiter = true;
            this._requestwaiting = false;
            try {
                await this.CancelAskWaiter();
            }
            finally {
                this._StopNearByWatch();
                this._heldWaiterPayTickets = [];
                this._waiterDismissed = true;
                this._cancellingWaiter = false;
                this.SetPanel('MENU');
            }
            return;
        }

        const _hadPending = this.HasWaiterRequest
            || this._heldWaiterPayTickets.some((ticket) => ticket.IsToPay)
            || this._OrphanPendingPayTickets(this.qrcode?.requests ?? []).length > 0;
        this._heldWaiterPayTickets = [];
        this._waiterDismissed = _hadPending;
        this.SetPanel('MENU');
    }

    private RevertTicketRequest(request: AskWaiterView): void {
        const _ticket = request.ticket;
        if (!_ticket || ((request.reason !== 'PAYCASH') && (request.reason !== 'PAYCARD'))) {
            return;
        }
        _ticket.CancelPayment();
        if (_ticket.IsPrepay) {
            _ticket.status = 'PR';
        }
    }

    /********************************/
    /* Ready overlay                */
    /********************************/

    get ShowReady(): boolean {
        return (this.qrcode?.ReadyTickets.length ?? 0) > 0;
    }

    /********************************/
    /* Action overlay               */
    /********************************/

    private _hiddenactions: Array<AskWaiterView> = [];
    private _showaction = false;

    get ShowAction(): boolean {
        return this._showaction;
    }

    private set ShowAction(value: boolean) {
        this._showaction = value;
        this.change.markForCheck();
    }

    private get UserActions(): Array<AskWaiterView> {
        if (this.place?.TableService) {
            return [];
        }
        return this.qrcode?.requests ?? [];
    }

    private get HasActions(): boolean {
        return this.UserActions.some((request) => this._hiddenactions.indexOf(request) === -1);
    }

    private AddHiddenActions(actions: Array<AskWaiterView>): void {
        for (const request of actions) {
            if (this._hiddenactions.indexOf(request) === -1) {
                this._hiddenactions.push(request);
            }
        }
    }

    private CheckAction(): void {
        if (this.HasActions && !this.ShowAction) {
            this.ShowAction = true;
        }
    }

    OnEndAction(): void {
        this.AddHiddenActions(this.UserActions);
        this.ShowAction = false;
    }

    OnReceipts(): void {
        this.SetPanel('RECEIPT');
    }

    OnClosePanel(): void {
        this._requestwaiting = false;
        this._ordertkt = null;

        if (this.place?.TableService && (this.qrcode?.requests.length ?? 0) > 0) {
            this._waiterDismissed = false;
            this.SetPanel('WAITER');
            void this.CheckAskWaiter();
            return;
        }

        this.SetPanel('MENU');
        this.CheckAction();
    }

    /********************************/
    /* Capability tiles             */
    /********************************/

    private _expandedOption: string | null = null;

    get ExpandedOption(): string | null {
        return this._expandedOption;
    }

    get OptionTiles(): Array<GuestOptionTile> {
        const tiles: Array<GuestOptionTile> = [];
        for (const opkey of this.DisplayOptions) {
            const tile = this._BuildOptionTile(opkey);
            if (tile) {
                tiles.push(tile);
            }
        }
        return tiles;
    }

    get ExpandedTile(): GuestOptionTile | null {
        const opkey = this._expandedOption;
        if (!opkey) {
            return null;
        }
        return this.OptionTiles.find((tile) => tile.opkey === opkey) ?? null;
    }

    OnToggleOption(opkey: string): void {
        this._expandedOption = this._expandedOption === opkey ? null : opkey;
        this.change.markForCheck();
    }

    OptionEnabled(opkey: string): boolean {
        const _place = this.place;
        if (!_place) {
            return false;
        }

        switch (opkey) {
            case 'optionTableService':
                return _place.TableService;
            case 'optionCreditCard':
                return _place.CanCard;
            case 'optionOnlinePay':
                return _place.CanLine;
            case 'optionQrOrders':
                return _place.AcceptQR;
            default:
                return false;
        }
    }

    OptionTrack(_index: number, tile: GuestOptionTile): string {
        return tile.opkey;
    }

    SocialTrack(_index: number, link: string): string {
        return link;
    }

    private _BuildOptionTile(opkey: string): GuestOptionTile | null {
        const _place = this.place;
        if (!_place) {
            return null;
        }

        const enabled = this.OptionEnabled(opkey);
        const currency = this.tr('@currency');

        switch (opkey) {
            case 'optionTableService':
                return this._WithToneBadge({
                    opkey,
                    icon: 'restaurant-outline',
                    labelKey: '@place_option_label_table',
                    titleKey: enabled
                        ? '@place_option_table_service_ok'
                        : '@place_option_table_service_nok',
                    messageKey: enabled
                        ? '@place_option_table_service_ok_message'
                        : '@place_option_table_service_nok_message',
                    messageArgs: [],
                    tone: enabled ? 'ok' : 'nok',
                });
            case 'optionCreditCard':
                return this._WithToneBadge({
                    opkey,
                    icon: 'card-outline',
                    labelKey: '@place_option_label_creditcard',
                    titleKey: enabled
                        ? '@place_option_creditcard_ok'
                        : '@place_option_creditcard_nok',
                    messageKey: enabled
                        ? (_place.CardMinimum === 0
                            ? '@place_option_creditcard_any'
                            : '@place_option_creditcard_min')
                        : '@place_option_creditcard_nok_message',
                    messageArgs: enabled && _place.CardMinimum !== 0
                        ? [_place.CardMinimum, currency]
                        : [],
                    tone: enabled ? 'ok' : 'nok',
                });
            case 'optionOnlinePay':
                return this._WithToneBadge({
                    opkey,
                    icon: 'globe-outline',
                    labelKey: '@place_option_label_payonline',
                    titleKey: enabled
                        ? '@place_option_payonline_ok'
                        : '@place_option_payonline_nok',
                    messageKey: enabled
                        ? (_place.OnlineMinimum === 0
                            ? '@place_option_payonline_any'
                            : '@place_option_payonline_min')
                        : '@place_option_payonline_nok_message',
                    messageArgs: enabled && _place.OnlineMinimum !== 0
                        ? [_place.OnlineMinimum, currency]
                        : [],
                    tone: enabled ? 'ok' : 'nok',
                });
            case 'optionQrOrders':
                return this._WithToneBadge({
                    opkey,
                    icon: 'phone-portrait-outline',
                    labelKey: '@place_option_label_qrorders',
                    titleKey: enabled
                        ? '@place_option_qrorders_ok'
                        : '@place_option_qrorders_nok',
                    messageKey: enabled
                        ? '@place_option_qrorders_ok_message'
                        : '@place_option_qrorders_nok_message',
                    messageArgs: [],
                    tone: enabled ? 'ok' : 'nok',
                });
            default:
                return null;
        }
    }

    private _WithToneBadge(
        tile: Omit<GuestOptionTile, 'badgeColor' | 'badgeName'>,
    ): GuestOptionTile {
        const tone = tile.tone;
        return {
            ...tile,
            badgeColor: tone === 'ok' ? 'success' : tone === 'nok' ? 'danger' : 'warning',
            badgeName: tone === 'ok' ? 'checkmark-circle' : tone === 'nok' ? 'close-circle' : 'alert-circle',
        };
    }
}
